<script lang="ts">
  /**
   * F1 · The kitchen tablet. One screen, no sign-in, no rail, no header.
   *
   * This is the highest-leverage item in the programme and the reason is not
   * technical: it converts the estate from a website somebody has to remember into
   * an object in the room they are already standing in. The failure mode the whole
   * programme is exposed to — *forgetting the system exists at the moment it would
   * have helped* — is the one thing a wall screen cannot suffer from.
   *
   * Four decisions follow from "it is a wall object, not a page", and each of them
   * is a departure from every other screen in this repo:
   *
   *  · **It never asks who you are.** It is authenticated by being in the house, and
   *    runs as the existing `display` role. `App.svelte` routes here *before* the
   *    sign-in gate, so the gate is not merely skipped — it is unreachable. A wall
   *    screen that shows a Google button has failed at the only job it has, because
   *    nobody is going to sign in to look at the weather on their way past.
   *  · **No chrome.** No `Shell`: no rail, no header, no search, no avatar. There is
   *    exactly one link on the screen and it is in the footer, quiet, for whoever is
   *    setting the tablet up. Everything else is text to be read from two metres.
   *  · **Bigger type, everywhere.** The type tokens are re-based upward on the root
   *    below — see the comment there. The estate's floor is a floor; this raises it.
   *  · **It refuses rather than guesses.** Most of what this screen is meant to show
   *    has no data source anywhere in the estate today. Those panels render
   *    an em dash and a labelled slot saying what would fill them. A plausible
   *    placeholder on a wall becomes what the household believes, and a wrong number
   *    here is worse than a blank one by exactly the amount of trust the screen has.
   *
   * Read `lib/tablet.svelte.ts` for which sources exist and which do not; the
   * reasoning about each one lives beside the declaration.
   *
   * ## What F7, F8 and F11 changed here
   *
   * The screen still has to fit one view with no scrolling — a card that has
   * scrolled off a wall tablet does not exist — so nothing was simply added:
   *
   *  · **F8's list took the "Tonight" slot.** Tonight had no source and never would
   *    have one of its own; the week now answers it. The list is the only panel on
   *    this screen anybody can *do* something with, which on a kitchen wall is worth
   *    more than a third read-only figure.
   *  · **F7's week is a ribbon under the clock**, about sixty pixels, rather than a
   *    sixth card. Seven dates on one line is still "the week on one screen".
   *  · **F11 swaps the ribbon and the big card round on a Sunday.** *"The tablet
   *    shows the week ahead rather than today's tasks"* — so on Sunday the week gets
   *    the card and what is left of the attention rail becomes the quiet line under
   *    the clock. The geometry is identical; only which thing is large changes.
   */
  import { Icon, Panel, DASH } from "@steyncd/steyn-design";
  import TheList from "../components/TheList.svelte";
  import Week from "../components/Week.svelte";
  import { lineFor } from "../lib/dailyline";
  import { glance } from "../lib/glance.svelte";
  import { heldSentence, isSunday, splitForSunday } from "../lib/sunday.svelte";
  import { SOURCES, REFRESH_MS, clock, longDate, tablet } from "../lib/tablet.svelte";
  import { deadlinesIn, week } from "../lib/week.svelte";

  // One loop for the whole screen, torn down with the component. `$effect` rather
  // than onMount so the teardown is the return value and cannot be forgotten.
  $effect(() => tablet.start());

  const verse = $derived(lineFor(new Date(tablet.now)));

  /** The one real reading this screen has: outside temperature, from the public feed. */
  const outside = $derived(glance.stat("outside"));

  /**
   * Never read, as distinct from read-and-failed.
   *
   * Only the first is worth taking the figure away for. If a temperature was read
   * twenty minutes ago and the last sweep failed, the useful thing to show is that
   * temperature with its age on it — not an error box where the number was. The lie
   * the acceptance list names is a cached figure presented as *live*, and the chip
   * beside it is what stops it being that.
   */
  const weatherError = $derived(glance.readAt === null && glance.error ? glance.error : null);
  const weatherStale = $derived(tablet.isStale(glance.readAt) || glance.serverStale);

  const attentionStale = $derived(tablet.attentionState === "ready" && tablet.isStale(tablet.attentionAt));

  /**
   * F11. Derived from `tablet.now`, which ticks every fifteen seconds — so a screen
   * that has been on the wall since Saturday afternoon becomes the Sunday screen at
   * midnight without anyone reloading it, and becomes the Monday one again the same
   * way. A wall tablet is the one surface where "it will be right after a refresh"
   * means "it will be wrong until somebody notices".
   */
  const isSun = $derived(isSunday(tablet.now));

  /**
   * On a Sunday, only what could not wait. `split.held` is the count the quiet line
   * states out loud — holding is not hiding, and a rail that silently emptied itself
   * one day a week is a rail nobody would trust the other six.
   */
  const split = $derived(splitForSunday(tablet.attention, tablet.now));
  const top = $derived(split.show[0] ?? null);

  /** F7. Which week, and what the estate already knows falls inside it. */
  const range = $derived(week.range(tablet.now));
  /**
   * Only ever true state. A 403 from `/attention` is not "nothing is due" — the week
   * has to be told the difference or its deadline strip would make a claim about the
   * estate out of a fact about this tablet's permissions.
   */
  const attentionKnown = $derived(tablet.attentionState === "ready");
  /**
   * The whole feed, not `split.show` — including anything F11 is holding.
   *
   * A deadline drawn on a week is reference, not a nudge: it says *the licence
   * expires on Thursday*, it does not ask anyone to do something now. F11 holds
   * nudges until Monday; hiding Thursday from a Sunday week would be the opposite of
   * "the tablet shows the week ahead".
   */
  const marks = $derived(attentionKnown ? deadlinesIn(tablet.attention, range.from) : []);

  const refreshMins = REFRESH_MS / 60_000;
</script>

<!-- `data-surface="home"` by hand: Shell normally sets it, and this screen has no
     Shell. Without it the tablet would inherit the operator defaults — mono figures,
     34px rows — which is the densest treatment in the estate on the screen that
     wants the loosest. -->
<div class="tablet" data-surface="home">
  <header class="band">
    <div>
      <div class="band__clock num">{clock(tablet.now)}</div>
      <div class="band__date">{longDate(tablet.now)}</div>
    </div>
    <!-- The daily line (F10) sits with the date rather than in a card of its own:
         it is the one thing here that is never stale, never unwired and never
         actionable, so it belongs with the things that are simply true. -->
    <blockquote class="line">
      <p class="line__t">{verse.text}</p>
      <cite class="line__ref">{verse.ref}</cite>
    </blockquote>
  </header>

  <!-- ── The strip under the clock ────────────────────────────────────────────
       Six days a week it is F7's week. On a Sunday the week moves into the big card
       and this carries what is left of the rail instead — which on a Sunday is
       supposed to be very little, and says how little out loud. -->
  <div class="strip">
    {#if !isSun}
      <Week variant="ribbon" now={tablet.now} {marks} marksKnown={attentionKnown} />
    {:else if tablet.attentionState === "ready"}
      <p class="quiet">
        <span class="quiet__t">{heldSentence(split.held.length)}</span>
        {#if top}
          <!-- Urgent and overdue never hold. A gate left open does not become a
               Monday problem because it is Sunday. -->
          <span class="mark"><Icon name="alert" size={15} /> {top.text} — from {top.source}</span>
        {/if}
      </p>
    {:else if tablet.attentionState === "denied"}
      <p class="quiet">
        <span class="quiet__t">
          Sunday. <strong>This screen may not read the attention feed</strong>, so it cannot say what
          is holding until Monday.
        </span>
      </p>
    {:else if tablet.attentionState === "failed"}
      <p class="quiet">
        <span class="mark"
          ><Icon name="alert" size={15} /> Sunday. {tablet.attentionError ?? "Could not reach Fabric"} — this
          is a failure to read, not a quiet day.</span
        >
      </p>
    {:else}
      <p class="quiet"><span class="quiet__t">Sunday. Reading the rail…</span></p>
    {/if}
  </div>

  <div class="grid">
    <!-- ── Outside ─────────────────────────────────────────────────────────── -->
    <!-- The only panel on this screen with a live source: `/public/glance`, which is
         unauthenticated by design and therefore answers a tablet that has never
         signed in. That is not a coincidence — it is the reason this panel could be
         built and the presence panel could not. -->
    <section class="card card--third">
      <h2 class="card__h">Outside</h2>
      <Panel error={weatherError} loading={glance.readAt === null && !glance.error} variant="text" rows={2}>
        {#snippet empty()}
          Nothing has been read yet.
          <span class="next">Check that this tablet can reach Fabric.</span>
        {/snippet}

        <!-- Em dash, never zero: a sensor that did not report is not a house at 0 °C. -->
        <p class="fig">{outside?.value ?? DASH}<span class="fig__u">°C</span></p>
        {#if weatherStale}
          {@render age(glance.readAt)}
        {/if}
        <!-- The rest of a weather panel — rain, wind, the day ahead — has no source.
             Named rather than left as blank space, so the gap reads as a decision. -->
        <p class="slot">
          <span class="slot__l">Rain, and the rest of the day</span>
          {DASH} no forecast is wired to the estate.
        </p>
      </Panel>
    </section>

    <!-- ── Who is where ────────────────────────────────────────────────────── -->
    <section class="card card--third">
      <h2 class="card__h">{SOURCES.presence.title}</h2>
      <p class="fig fig--slot">{DASH}</p>
      <Panel isEmpty>
        {#snippet empty()}
          {SOURCES.presence.why}
          <span class="next">{SOURCES.presence.next}</span>
        {/snippet}
        {#snippet children()}{/snippet}
      </Panel>
    </section>

    <!-- ── The list ────────────────────────────────────────────────────────── -->
    <!-- F8, and the only panel on this screen anybody can act on. It took the slot
         "Tonight" used to hold: Tonight had no source of its own and the week under
         the clock now answers it, and a third read-only figure on a kitchen wall is
         worth less than one thing a person can add milk to on the way past.
         It needs no sign-in, which is what makes it work here at all. -->
    <section class="card card--third card--do">
      <h2 class="card__h">The list</h2>
      <TheList wall />
    </section>

    <!-- ── The big card ────────────────────────────────────────────────────── -->
    <!-- F11's swap. Six days it is "the one thing that wants attention" — singular on
         purpose, because a list of five on a wall is a list nobody reads. On a Sunday
         it is the week ahead instead, which is the whole of *"the tablet shows the
         week ahead rather than today's tasks"*. -->
    <section class="card card--main">
      {#if isSun}
        <h2 class="card__h">{range.label}</h2>
        <Week now={tablet.now} {marks} marksKnown={attentionKnown} compact notes={false} />
      {:else}
        <h2 class="card__h">Wants someone</h2>
        <Panel
          error={tablet.attentionState === "failed" ? tablet.attentionError : null}
          loading={tablet.attentionState === "loading"}
          isEmpty={tablet.attentionState === "denied" || (tablet.attentionState === "ready" && !top)}
        >
          {#snippet empty()}
            {#if tablet.attentionState === "denied"}
              <!-- The most important sentence on this screen. A 403 must never read as
                   "nothing wants you": one is a fact about this tablet's permissions,
                   the other is a claim about the house, and the claim would be false on
                   exactly the mornings it mattered. -->
              <strong>This screen may not read the attention feed.</strong> That is not the
              same as nothing waiting.
              <span class="next">
                {tablet.unpaired
                  ? "This tablet has never been paired. Sign in on it once as the display account."
                  : "Fabric serves attention to partner and owner only. Allow display, or read it on the Front Door."}
              </span>
            {:else}
              Nothing is waiting. Vault, Homestead and Waypoint post here when something
              needs a person — an expiring licence, a job that is due.
              <span class="next">Nothing to do. This is the good state.</span>
            {/if}
          {/snippet}

          {#if top}
            <p class="want">{top.text}</p>
            <p class="want__from">
              {#if top.overdue}
                <span class="mark"><Icon name="alert" size={15} /> overdue</span>
              {/if}
              from {top.source}
            </p>
            {#if split.show.length > 1}
              <p class="more">and {split.show.length - 1} more on the Front Door</p>
            {/if}
            {#if attentionStale}
              {@render age(tablet.attentionAt)}
            {/if}
          {/if}
        </Panel>
      {/if}
    </section>

    <!-- ── The boys ────────────────────────────────────────────────────────── -->
    <section class="card card--side">
      <h2 class="card__h">{SOURCES.chores.title}</h2>
      <!-- Two named slots rather than one, because the shape of what is missing is
           itself information: a list each and a balance each, not one shared list. -->
      <div class="boys">
        <div class="boy"><span class="fig fig--slot">{DASH}</span><span class="boy__l">Liam · balance</span></div>
        <div class="boy"><span class="fig fig--slot">{DASH}</span><span class="boy__l">Eben · balance</span></div>
      </div>
      <Panel isEmpty>
        {#snippet empty()}
          {SOURCES.chores.why}
          <span class="next">{SOURCES.chores.next}</span>
        {/snippet}
        {#snippet children()}{/snippet}
      </Panel>
    </section>
  </div>

  <footer class="foot">
    <!-- One freshness statement for the whole screen, rather than a tick on every
         card. A mark beside five things that are all fine is noise, and noise is how
         a household learns to stop reading the marks that are not. -->
    <span class="foot__age">
      {#if tablet.oldestReadAt === null}
        <Icon name="pause" size={15} /> Nothing read yet
      {:else if tablet.isStale(tablet.oldestReadAt)}
        {@render age(tablet.oldestReadAt)}
      {:else}
        Read {clock(tablet.oldestReadAt)} · refreshes every {refreshMins} minutes
      {/if}
    </span>
    <a class="foot__link" href="#/">Front Door</a>
  </footer>
</div>

<!-- Stale, said in three signals: the clock glyph, the word, and amber. Never a bare
     coloured dot, and never a colour on its own — a greyscale photograph of this
     screen still carries the meaning. -->
{#snippet age(at: number | null)}
  <span class="mark"><Icon name="clock" size={15} /> stale · last read {at ? clock(at) : DASH}</span>
{/snippet}

<style>
  /* ── The screen ────────────────────────────────────────────────────────────
     A wall object. It fills the viewport, does not scroll in the landscape case it
     was designed for, and paints the estate background itself because there is no
     Shell here to do it. */
  .tablet {
    /* The type tokens, re-based for viewing distance.
       These are the estate's own tokens, raised — not new sizes invented locally.
       `--fs-micro` is a *floor*, and the acceptance rule is that nothing goes below
       it; raising it on a screen read from two metres is that rule working rather
       than an exception to it. Everything inside, including `Panel`'s own copy,
       picks these up through var() without knowing it is on a tablet. */
    --fs-micro: 0.95rem;
    --fs-small: 1.05rem;
    --fs-body: 1.15rem;

    min-height: 100vh;
    display: grid;
    /* Band, the strip under it, the cards, the footer. The strip is a row of its own
       rather than part of the band so that whichever of the two things is in it —
       the week on a weekday, the quiet rail on a Sunday — sits at the same height
       and the screen does not appear to re-lay itself once a week. */
    grid-template-rows: auto auto 1fr auto;
    /* Both of these came down a notch when the strip arrived: four rows share the
       height where three did, so the gaps are paid three times instead of twice.
       Measured at 1280×800 and 1024×768 — the screen fits one view at both, which is
       the constraint the whole layout exists to meet. */
    gap: clamp(10px, 1.5vh, 20px);
    /* `min(vw, vh)` rather than vw alone, here and on the clock below.
       A wall tablet is height-constrained, not width-constrained: at 1280×800 a
       width-only scale gives 33px of padding and an 80px clock, and the last card
       falls off the bottom — which on a screen nobody scrolls is the same as not
       rendering it. Sizing to the smaller dimension keeps the whole thing on one
       view at every landscape ratio a mounted tablet actually has. */
    padding: clamp(14px, min(2.6vw, 2.4vh), 30px);
    background: var(--aurora);
    color: var(--tx);
    font-size: var(--fs-body);
  }

  /* ── The band: time, date, and the daily line ──────────────────────────── */
  .band {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: clamp(20px, 4vw, 64px);
    flex-wrap: wrap;
  }
  .band__clock {
    /* The largest thing on the screen, and the only one legible from the far side of
       the kitchen. It is also the one figure here that can never be wrong. */
    font-family: var(--font-heading);
    font-size: clamp(2.4rem, min(6.5vw, 9vh), 4.5rem);
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.03em;
  }
  .band__date {
    margin-top: 6px;
    font-size: clamp(1rem, min(1.8vw, 2.4vh), 1.4rem);
    color: var(--tx2);
  }
  .line {
    margin: 0;
    /* Wide rather than tall. The longest curated line runs to about 150 characters,
       and at 38ch that is four stacked lines pushing the cards off a 800px-tall
       screen — which is the whole failure this screen cannot have. */
    max-width: 62ch;
    text-align: right;
  }
  .line__t {
    margin: 0;
    font-family: var(--font-serif);
    font-size: clamp(1.05rem, 1.5vw, 1.4rem);
    line-height: 1.35;
    color: var(--tx2);
  }
  .line__ref {
    display: block;
    margin-top: 8px;
    font-size: var(--fs-micro);
    font-weight: 700;
    font-style: normal;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--mut);
  }

  /* ── The five cards ───────────────────────────────────────────────────────
     Six columns so three-across and two-across share one grid. A media query rather
     than a container query, deliberately: this route *is* the viewport — there is no
     Shell and no container to ask. Container queries are for panels inside a shell,
     which is exactly what this is not. */
  .grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: clamp(12px, 1.4vw, 20px);
    align-content: start;
  }
  .card {
    display: flex;
    flex-direction: column;
    gap: clamp(6px, 1.1vh, 10px);
    padding: clamp(14px, min(1.6vw, 2.2vh), 24px);
    border-radius: var(--r-surface);
    background: var(--s1);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .card--third {
    grid-column: span 2;
  }
  /* Four and two rather than the three and three this row used to be. Measured
     rather than guessed: at three the week's seven day columns overflow their own
     card at 1280px and the row grows to 296px, where at four the week fits and the
     boys' two balances lose nothing they were using. */
  .card--main {
    grid-column: span 4;
  }
  .card--side {
    grid-column: span 2;
  }
  /* The one card whose height is unbounded, because it holds however many things
     somebody has typed. It scrolls inside itself rather than growing: the rule this
     screen lives by is that the *page* never scrolls, and a shopping list that
     pushed the boys' balances off the wall would be the failure that rule exists to
     prevent. */
  .card--do {
    /* Sized against the row it is in, not against its contents. Two card rows, a
       band, a strip and a footer have to share 800px on the screen this was designed
       for, which leaves a little over 200px a row — so the cap is what keeps a long
       shopping list from stretching its row and pushing the second one off. */
    max-height: min(27vh, 230px);
    overflow-y: auto;
  }
  .card__h {
    margin: 0;
    font-size: var(--fs-micro);
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--mut);
  }

  /* ── Figures ──────────────────────────────────────────────────────────────
     The design system's own `.fig` treatment with only the size raised, for the same
     viewing-distance reason as the type tokens above. */
  .fig {
    margin: 0;
    font-family: var(--font-figure);
    font-size: clamp(1.9rem, min(4.4vw, 5.2vh), 3.2rem);
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.02em;
    color: var(--tx);
  }
  .fig__u {
    margin-left: 0.1em;
    font-size: 0.42em;
    font-weight: 600;
    letter-spacing: 0;
    color: var(--mut);
  }
  /* An em dash standing in for a figure that has no source. Muted rather than
     full-strength text: it is not a reading, and it should not read like one. */
  .fig--slot {
    color: var(--mut);
  }

  .slot {
    margin: 0;
    font-size: var(--fs-micro);
    line-height: 1.45;
    color: var(--mut);
  }
  .slot__l {
    font-weight: 800;
    color: var(--tx2);
  }

  /* The next action inside an empty state. A block, so it never reads as part of the
     sentence explaining why the panel is empty — one says what is true, the other
     says what to do about it. */
  .next {
    display: block;
    margin-top: 6px;
    font-weight: 700;
    color: var(--tx2);
  }

  /* ── Attention ─────────────────────────────────────────────────────────── */
  .want {
    margin: 0;
    font-family: var(--font-serif);
    font-size: clamp(1.2rem, 2vw, 1.75rem);
    line-height: 1.3;
    color: var(--tx);
  }
  .want__from,
  .more {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    font-size: var(--fs-small);
    color: var(--mut);
  }

  /* ── The boys ──────────────────────────────────────────────────────────── */
  .boys {
    display: flex;
    gap: clamp(20px, 4vw, 56px);
  }
  .boy {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .boy__l {
    font-size: var(--fs-micro);
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--mut);
  }

  /* ── The strip under the clock ─────────────────────────────────────────── */
  .quiet {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 18px;
    margin: 0;
  }
  .quiet__t {
    font-size: var(--fs-small);
    color: var(--tx2);
  }

  /* ── Status marks ──────────────────────────────────────────────────────── */
  .mark {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: var(--fs-micro);
    font-weight: 800;
    color: var(--warn);
  }

  /* ── Footer ────────────────────────────────────────────────────────────── */
  .foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding-top: 10px;
    border-top: 1px solid var(--line);
    font-size: var(--fs-micro);
    color: var(--mut);
  }
  .foot__age {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  /* The only link on the screen, and it is for whoever is setting the tablet up
     rather than for the household. Kept a real anchor so it is keyboard reachable
     and picks up the global focus ring: "no chrome" must not mean "no way out". */
  .foot__link {
    padding: 6px 10px;
    border-radius: var(--r-pill);
    color: var(--mut);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .foot__link:hover {
    color: var(--tx);
  }

  /* Shorter landscape screens — a 1024×768 iPad in a wall mount is the common one.
     The type steps down by roughly one notch, which is still well above the estate's
     floor and still legible at arm's length, and it is what keeps the fifth card on
     the screen. A card that has scrolled off a wall tablet is a card that does not
     exist, so height is the constraint that wins here. */
  @media (max-height: 820px) {
    .tablet {
      --fs-micro: 0.85rem;
      --fs-small: 1rem;
      --fs-body: 1.05rem;
      /* The design system's spacing tokens, not local overrides: `Panel`'s own
         padding is `var(--sp-4)`, and the two unwired panels on this screen are the
         single largest block of vertical space on it. Tightening the token tightens
         them without this file reaching inside another component's styles. */
      --sp-3: 8px;
      --sp-4: 10px;
      gap: 9px;
      padding: 14px;
    }
    /* Measured at 1024×768, which is an iPad in a wall mount and the tightest screen
       this has to fit. Six cards, a band, a strip and a footer come to exactly the
       viewport at these values; everything above is what was traded to get there,
       and nothing went below the estate's 11.5px floor — 0.85rem is 13.6px. */
    .card {
      padding: 10px 12px;
      gap: 5px;
    }
    .fig {
      font-size: 1.95rem;
    }
    .line__t {
      font-size: 1.05rem;
    }
  }

  /* ── Portrait, and small tablets ───────────────────────────────────────────
     Landscape is what this was designed for and what a kitchen mount uses. Held
     portrait it becomes one column and is allowed to scroll — a squeezed six-column
     grid at 800px wide is unreadable at arm's length, which is the only thing this
     screen has to be. */
  @media (max-width: 900px), (orientation: portrait) {
    .grid {
      grid-template-columns: 1fr;
    }
    .card--third,
    .card--main,
    .card--side {
      grid-column: 1 / -1;
    }
    .band {
      flex-direction: column;
    }
    .line {
      text-align: left;
    }
  }

  /* Nothing on this screen animates or transitions, so there is nothing to disable
     under `prefers-reduced-motion`. That is deliberate rather than accidental: a
     wall screen in the corner of the eye that moves is a wall screen that gets
     turned off. */
</style>
